import React, { createContext, useState } from 'react';
import {
	ICart,
	ICartInfo,
	ICartItem,
	IDates,
	IOrderInfoResponse,
	IPrice,
	ISelectedCountry,
	ITollSectionOffer,
	IVehicleCategory,
	IVignetteContext,
	IVignetteOffer,
	ProductType,
} from '../models';
import { ITypeVignetteOrderingProcessFields } from '../contentful-types';
import { calculateNewDates, transformShortTextLabelsToObject } from '../helper-fns';

interface IVignetteProviderProps {
	children: React.ReactNode;
}

export const VignetteContext = createContext<IVignetteContext>({
	vehicleCategory: undefined,
	selectedProducts: [],
	selectedSectionTolls: [],
	selectedCountries: [],
	productsTypeMap: new Map(),
	cartItems: [],
	cartInfo: undefined,
	cartPrice: undefined,
	selectedCartItem: undefined,
	orderData: undefined,
	isTransitionPeriod: false,
	contentfulLabels: undefined,
	platePubliclyVisible: false,
	updateSelectedCartItem: () => {},
	updateSelectedProducts: () => {},
	removeSelectedProductsBySupplierId: () => {},
	removeSelectedProductsBySupplierAndCategory: () => {},
	initSelectedCountries: () => {},
	updateSelectedCountries: () => {
		return [];
	},
	removeCountryBySupplierId: () => {},
	updateSelectedProductsDates: () => {},
	deleteSelectedProducts: () => {},
	addSelectedSectionTolls: () => {},
	updateSelectedSectionTolls: () => {},
	removeSelectedSectionTollsById: () => {},
	deleteSelectedSectionTolls: () => {},
	updateWithDatesAndPlates: () => {},
	updatePlates: () => {},
	removeSelectedProductsDates: () => {},
	updateVehicleCategory: () => {},
	updateCart: () => {},
	updateNewOrderData: () => {},
	updateIsTransitionPeriod: () => {},
	updateContentfulLabels: () => {},
	setPlatePubliclyVisible: () => {},
});

VignetteContext.displayName = 'VignetteContext';

export const VignetteProvider = ({ children }: IVignetteProviderProps) => {
	const initProductMap = new Map([
		['autVignette', false],
		['sloVignette', false],
		['sectionToll', false],
		['flexProduct', false],
		['schweiz', false],
	] as [ProductType, boolean][]);
	const [vehicleCategory, setVehicleCategory] = useState<IVehicleCategory | undefined>(undefined);
	const [selectedProducts, setSelectedProducts] = useState<IVignetteOffer[] | []>([]);
	const [selectedCountries, setSelectedCountries] = useState<ISelectedCountry[] | []>([]);
	const [selectedSectionTolls, setSelectedSectionTolls] = useState<ITollSectionOffer[] | []>([]);
	const [productsTypeMap, setProductsTypeMap] = useState<Map<ProductType, boolean>>(initProductMap);
	const [selectedCartItem, setSelectedCartItem] = useState<ICartItem>();
	const [cartItems, setCartItems] = useState<ICartItem[]>([]);
	const [cartPrice, setCartPrice] = useState<IPrice | undefined>(undefined);
	const [cartInfo, setCartInfo] = useState<ICartInfo | undefined>(undefined);
	const [orderInfo, setOrderInfo] = useState<IOrderInfoResponse>();
	const [isTransitionPeriod, setIsTransitionPeriod] = useState<boolean>(false);
	const [platePubliclyVisible, setPlatePubliclyVisible] = useState(false);
	const [contentfulLabels, setVignetteConfigurationLabels] = useState<ITypeVignetteOrderingProcessFields | undefined>(
		undefined,
	);

	const productSuppIdMap = {
		autVignette: 3,
		sloVignette: 7,
		sectionToll: 3,
		flexProduct: 3,
		schweiz: 14,
	};

	const moduleTypeMap = {
		2: 2,
		3: 3,
		4: 3,
		14: 14,
	};

	const detectCorrespodingProduct = (supplierId: number, moduleTypeId?: number) => {
		let product;
		if (supplierId === 3) {
			switch (moduleTypeId) {
				case 2:
					product = 'autVignette';
					break;
				case 3:
					product = 'sectionToll';
					break;
				case 4:
					product = 'flexProduct';
					break;
				default:
					product = '';
			}
		}

		if (supplierId === 7) {
			product = 'sloVignette';
		}

		if (supplierId === 14) {
			product = 'schweiz';
		}

		return product;
	};

	const updateVehicleCategory = (newVehicleCategory?: IVehicleCategory) => {
		setVehicleCategory(newVehicleCategory);
	};

	const updateSelectedCountries = (newCountry: ISelectedCountry): ISelectedCountry[] => {
		const countryAlreadySelected = selectedCountries.find(({ supplierId }) => supplierId === newCountry.supplierId);
		let newSelectedCountries: ISelectedCountry[] = [];
		if (!countryAlreadySelected) {
			newSelectedCountries = [...selectedCountries, newCountry];
			setSelectedCountries(newSelectedCountries);
		} else {
			newSelectedCountries = selectedCountries.filter(({ supplierId }) => supplierId !== newCountry.supplierId);
			setSelectedCountries(newSelectedCountries);
		}
		return newSelectedCountries;
	};

	const removeCountryBySupplierId = (supplierIdToRemove: number) => {
		const filteredCountries = selectedCountries.filter(({ supplierId }) => supplierId !== supplierIdToRemove);
		setSelectedCountries(filteredCountries);
	};

	const initSelectedCountries = (newCountries: ISelectedCountry[]) => {
		setSelectedCountries(newCountries);
	};

	const updateProductsByCorrectData = (vignette: IVignetteOffer, updatedVignette) => {
		const refSelectedProduct =
			selectedProducts.find(({ moduleTypeId }) => moduleTypeId === 2) || selectedProducts[0]; //all products use same dates and plates
		return {
			...updatedVignette,
			dates: calculateNewDates(vignette?.dates?.start || new Date(), vignette),
			plates: refSelectedProduct.plates,
			countryCode: refSelectedProduct.countryCode,
			licenseplateType: refSelectedProduct.licenseplateType,
		};
	};

	// TODO: We could refactor this, as it has become very complex
	const updateSelectedProducts = (vignette: IVignetteOffer, multiSelect = false) => {
		// Covering case when plates are reused in the configuration.
		let updatedVignette: IVignetteOffer =
			vignette?.moduleTypeId === 2
				? {
						...vignette,
						dates: calculateNewDates(new Date(), vignette),
				  }
				: vignette;
		if (vehicleCategory?.licenseplate) {
			updatedVignette = {
				...updatedVignette,
				// No need to format, as the plates already come from the Cart.
				plates: [vehicleCategory.licenseplate],
				licenseplateType: vehicleCategory.licenseplateType,
				countryCode: vehicleCategory.countryCode,
			};
		}

		// add first Product case
		if (!selectedProducts.length) {
			setProductsTypeMap((prevMap) => {
				const updatedMap = structuredClone(prevMap);
				const { supplierId, moduleTypeId } = vignette;
				const prod = detectCorrespodingProduct(supplierId, moduleTypeId);
				updatedMap.set(prod, true);
				return updatedMap;
			});
			setSelectedProducts([updatedVignette]);
			return;
		}

		const correspondingProduct = selectedProducts.find(({ supplierId, moduleTypeId }) => {
			const isSameSupplier = supplierId === updatedVignette.supplierId;
			const isSameModuleType = multiSelect
				? moduleTypeMap[updatedVignette.moduleTypeId] === moduleTypeMap[moduleTypeId]
				: true;
			return isSameSupplier && isSameModuleType;
		});

		// add new Product case
		if (!correspondingProduct) {
			setSelectedProducts((prev) =>
				!prev.find((p) => p.matchCode === updatedVignette.matchCode) ? [...prev, updatedVignette] : [...prev],
			);
			setProductsTypeMap((prevMap) => {
				const updatedMap = structuredClone(prevMap);
				const { supplierId, moduleTypeId } = vignette;
				const prod = detectCorrespodingProduct(supplierId, moduleTypeId);
				updatedMap.set(prod, true);
				return updatedMap;
			});
			return;
		}

		// replace existing Product case
		setSelectedProducts((prev) => [
			...prev.filter(
				({ supplierId, moduleTypeId }) =>
					correspondingProduct.supplierId !== supplierId ||
					moduleTypeMap[correspondingProduct.moduleTypeId] !== moduleTypeMap[moduleTypeId],
			),
			updateProductsByCorrectData(vignette, updatedVignette),
		]);
		setProductsTypeMap((prevMap) => {
			const updatedMap = structuredClone(prevMap);
			const { supplierId, moduleTypeId } = vignette;
			const prod = detectCorrespodingProduct(supplierId, moduleTypeId);
			const corrProd = detectCorrespodingProduct(
				correspondingProduct.supplierId,
				correspondingProduct.moduleTypeId,
			);
			updatedMap.set(corrProd, false);
			updatedMap.set(prod, true);
			return updatedMap;
		});
	};

	const removeSelectedProductsBySupplierId = (currSupplierId: number) => {
		setSelectedProducts((prev) => prev.filter(({ supplierId }) => currSupplierId !== supplierId));
		setProductsTypeMap((prev) => {
			const updatedMap = structuredClone(prev);
			updatedMap.forEach((_, prod) => {
				const currId = productSuppIdMap[prod];
				if (currId === currSupplierId) updatedMap.set(prod, false);
			});
			return updatedMap;
		});
	};

	const removeSelectedProductsBySupplierAndCategory = (currSupplierId: number, categoryId: number) => {
		setSelectedProducts((prev) =>
			prev.filter(
				({ supplierId, moduleTypeId }) =>
					currSupplierId === supplierId && moduleTypeMap[categoryId] !== moduleTypeMap[moduleTypeId],
			),
		);
		setProductsTypeMap((prev) => {
			const updatedMap = structuredClone(prev);
			updatedMap.forEach((_, prod) => {
				const currProd = detectCorrespodingProduct(currSupplierId, categoryId);
				if (currProd === prod) {
					updatedMap.set(prod, false);
				}
			});
			return updatedMap;
		});
	};
	const updateSelectedProductsDates = (newStartDate: Date | undefined) => {
		if (newStartDate) {
			setSelectedProducts((prev: IVignetteOffer[]) =>
				prev.map((offer) => {
					// Prevent Schweiz date change
					if (offer.supplierId === 14) {
						return offer;
					}

					if (offer.moduleTypeId === 2) {
						return { ...offer, dates: calculateNewDates(new Date(newStartDate), offer) };
					}

					return offer;
				}),
			);
		} else {
			setSelectedProducts((prev: IVignetteOffer[]) =>
				prev.map((offer) => {
					// Prevent Schweiz date change
					if (offer.supplierId === 14) {
						return offer;
					}

					delete offer.dates;
					return offer;
				}),
			);
		}
	};

	const removeSelectedProductsDates = () => {
		setSelectedProducts((prev) =>
			prev.map((offer) => {
				delete offer.dates;
				return offer;
			}),
		);
	};

	const updatePlates = (plates: string[], countryCode: string, licenseplateType: number) => {
		setSelectedProducts((prev: IVignetteOffer[]) =>
			prev.map((offer) => ({
				...offer,
				plates,
				countryCode,
				licenseplateType,
			})),
		);
		setSelectedSectionTolls((prev: IVignetteOffer[]) =>
			prev.map((offer) => ({
				...offer,
				plates,
				countryCode,
				licenseplateType,
			})),
		);
	};

	const updateWithDatesAndPlates = (dates: IDates, plates: string[]) => {
		setSelectedProducts((prev: IVignetteOffer[]) => prev.map((offer) => ({ ...offer, dates, plates })));
	};

	const deleteSelectedProducts = () => {
		setSelectedProducts([]);
		setProductsTypeMap(initProductMap);
	};

	const addSelectedSectionTolls = (vignetteTollSection: ITollSectionOffer) => {
		let sectionToll = vignetteTollSection;
		if (sectionToll !== undefined && vehicleCategory?.licenseplate) {
			sectionToll = {
				...vignetteTollSection,
				// No need to format, as the plates already come from the Cart.
				plates: [vehicleCategory.licenseplate],
				licenseplateType: vehicleCategory.licenseplateType,
				countryCode: vehicleCategory.countryCode,
			};
		}
		if (sectionToll)
			setSelectedSectionTolls((prevVignetteTollSections) => {
				if (
					prevVignetteTollSections.length > 0 &&
					prevVignetteTollSections.find((prevVignette) => prevVignette.matchCode === sectionToll.matchCode)
				) {
					return [...prevVignetteTollSections];
				}
				return [...prevVignetteTollSections, sectionToll];
			});
	};

	const removeSelectedSectionTollsById = (id: number) => {
		setSelectedSectionTolls(selectedSectionTolls.filter(({ serviceCatalogId }) => serviceCatalogId !== id));
	};

	const deleteSelectedSectionTolls = () => {
		setSelectedSectionTolls([]);
	};

	const updateSelectedSectionTolls = (currTollSecionOffers: ITollSectionOffer[]) => {
		const updatedTollSectionOffers = currTollSecionOffers.filter((offer) =>
			selectedSectionTolls.find(({ serviceCatalogCode }) => offer.serviceCatalogCode === serviceCatalogCode),
		);
		setSelectedSectionTolls(updatedTollSectionOffers);
	};

	const updateSelectedCartItem = (currCartItem?: ICartItem) => {
		setSelectedCartItem(currCartItem);
		sessionStorage.setItem('offerHash', currCartItem?.offerHash ?? '');
	};

	const updateCart = (cart?: ICart) => {
		if (!cart) {
			setCartItems([]);
			setCartPrice(undefined);
			setCartInfo(undefined);
			return;
		}
		const {
			cartDetails,
			price,
			sumArticlelPrice,
			sumDiscount,
			sumServiceFee,
			forceInvoice,
			flexProduct,
			paymentMethods,
		} = cart;
		setCartItems(cartDetails);
		setCartPrice(price);
		setCartInfo({ sumArticlelPrice, sumDiscount, sumServiceFee, forceInvoice, flexProduct, paymentMethods });
	};

	const updateIsTransitionPeriod = (bool: boolean) => {
		setIsTransitionPeriod(bool);
	};

	const updateNewOrderInfo = (currOrderInfo: IOrderInfoResponse) => {
		setOrderInfo(currOrderInfo);
	};

	const updateContentfulLabels = (labels?: ITypeVignetteOrderingProcessFields) => {
		if (labels?.shortTextLabels) {
			const shortTextLabels = transformShortTextLabelsToObject(labels.shortTextLabels.items);
			labels = { ...labels, ...shortTextLabels };
		}

		setVignetteConfigurationLabels(labels);
	};

	return (
		<VignetteContext.Provider
			value={{
				selectedProducts,
				selectedSectionTolls,
				selectedCountries,
				vehicleCategory,
				productsTypeMap,
				cartItems,
				cartPrice,
				cartInfo,
				selectedCartItem,
				orderData: orderInfo,
				isTransitionPeriod,
				contentfulLabels,
				platePubliclyVisible,
				updateVehicleCategory,
				updateSelectedProducts,
				initSelectedCountries,
				updateSelectedCountries,
				removeCountryBySupplierId,
				updateSelectedCartItem,
				removeSelectedProductsBySupplierId,
				removeSelectedProductsBySupplierAndCategory,
				deleteSelectedProducts,
				addSelectedSectionTolls,
				updateSelectedSectionTolls,
				removeSelectedSectionTollsById,
				deleteSelectedSectionTolls,
				updateWithDatesAndPlates,
				updateSelectedProductsDates,
				removeSelectedProductsDates,
				updatePlates,
				updateCart,
				updateNewOrderData: updateNewOrderInfo,
				updateIsTransitionPeriod,
				updateContentfulLabels,
				setPlatePubliclyVisible,
			}}>
			{children}
		</VignetteContext.Provider>
	);
};
